import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listUserDriverVerifications,
  reviewDriverVerification,
} from 'features/admin/services/adminApi';
import { formatVehicleType } from 'features/drivers/lib/driverLicenceStatus';
import { queryKeys } from 'core/query/queryKeys';
import { ROUTES } from 'core/router/routes';
import type { DriverLicenceRow, PlatformDriverKycProfile } from 'types/api.generated';

const VERIFICATION_BADGE: Record<string, string> = {
  unverified: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
  expired: 'bg-orange-50 text-orange-700',
};

type PendingReview = {
  profile: PlatformDriverKycProfile;
  verificationId: string;
  vehicleType: DriverLicenceRow['vehicleType'];
  documentUrl?: string;
};

export default function AdminUserDetail() {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [review, setReview] = useState<PendingReview | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const kycQuery = useQuery<PlatformDriverKycProfile[]>({
    queryKey: queryKeys.admin.userDriverVerifications(userId),
    queryFn: () => listUserDriverVerifications(userId),
    enabled: Boolean(userId),
  });

  const reviewMutation = useMutation({
    mutationFn: (verificationStatus: 'verified' | 'rejected') => {
      if (!review) throw new Error('Missing review context');
      return reviewDriverVerification(review.profile.profileId, {
        verificationId: review.verificationId,
        verificationStatus,
        rejectionReason:
          verificationStatus === 'rejected' ? reason.trim() : undefined,
      });
    },
    onSuccess: (_, status) => {
      if (review) {
        setMessage(
          `${formatVehicleType(review.vehicleType)} licence ${status} for ${review.profile.organizationName}.`,
        );
      }
      setReview(null);
      setReason('');
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.userDriverVerifications(userId),
      });
    },
    onError: () => {
      setMessage('Review failed. Try again.');
    },
  });

  const submitReview = (verificationStatus: 'verified' | 'rejected') => {
    if (verificationStatus === 'rejected' && !reason.trim()) {
      setMessage('Rejection reason is required.');
      return;
    }
    setMessage(null);
    reviewMutation.mutate(verificationStatus);
  };

  if (!userId) {
    return (
      <p className="p-10 text-center text-slate-500">User not specified.</p>
    );
  }

  return (
    <div className="min-h-screen bg-background-light px-6 py-10 font-display">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN.USERS)}
            className="text-sm font-bold text-primary mb-2"
          >
            ← Users
          </button>
          <h1 className="text-2xl font-black">Driver licence / KYC</h1>
          <p className="text-sm text-slate-600 mt-1 font-mono">{userId}</p>
        </div>

        {kycQuery.isLoading && (
          <p className="text-sm text-slate-500">Loading KYC profiles…</p>
        )}
        {kycQuery.isError && (
          <p className="text-sm text-red-600">Could not load driver verifications.</p>
        )}

        {kycQuery.data?.length === 0 && !kycQuery.isLoading && (
          <p className="text-sm text-slate-500">No driver profiles for this user.</p>
        )}

        <ul className="space-y-4">
          {kycQuery.data?.map((profile) => (
            <li
              key={profile.profileId}
              className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-bold text-slate-900">
                    {profile.organizationName}
                  </p>
                  <p className="text-xs text-slate-500">{profile.organizationSlug}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.deletedAt && (
                    <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                      Archived
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${
                      VERIFICATION_BADGE[profile.verificationStatus] ??
                      VERIFICATION_BADGE.unverified
                    }`}
                  >
                    {profile.verificationStatus.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {profile.verifications.length === 0 ? (
                <p className="text-sm text-slate-500">No licence submissions.</p>
              ) : (
                <ul className="divide-y border rounded-xl">
                  {profile.verifications.map((v) => (
                    <li
                      key={v.id}
                      className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span className="font-semibold">
                        {formatVehicleType(v.vehicleType)}
                      </span>
                      <span className="text-xs font-bold uppercase text-slate-600">
                        {v.status}
                      </span>
                      {v.documents[0] && (
                        <a
                          href={v.documents[0].documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-bold text-primary w-full"
                        >
                          View document
                        </a>
                      )}
                      {v.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => {
                            setReview({
                              profile,
                              verificationId: v.id,
                              vehicleType: v.vehicleType,
                              documentUrl: v.documents[0]?.documentUrl,
                            });
                            setReason('');
                            setMessage(null);
                          }}
                          className="text-xs font-bold text-primary"
                        >
                          Review
                        </button>
                      )}
                      {v.rejectionReason && (
                        <p className="text-xs text-red-700 w-full">
                          {v.rejectionReason}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {review && (
          <div className="bg-white border rounded-2xl p-6 space-y-3">
            <h2 className="font-bold">
              Review {formatVehicleType(review.vehicleType)} —{' '}
              {review.profile.organizationName}
            </h2>
            {review.documentUrl && (
              <a
                href={review.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-primary"
              >
                View submitted document
              </a>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Rejection reason (required when rejecting)"
              className="w-full rounded-xl border px-3 py-2 text-sm"
              rows={2}
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={reviewMutation.isPending}
                onClick={() => submitReview('verified')}
                className="bg-emerald-600 text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={reviewMutation.isPending}
                onClick={() => submitReview('rejected')}
                className="bg-red-600 text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => setReview(null)}
                className="border px-4 py-2 rounded-xl text-sm font-bold text-slate-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {message && <p className="text-sm">{message}</p>}

        <Link
          to={ROUTES.ADMIN.DRIVERS}
          className="block text-center text-sm text-slate-600"
        >
          Manual lookup (org + user id)
        </Link>
      </div>
    </div>
  );
}
